import React, { useEffect, useState, useCallback } from 'react';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { Ticket, Zap, Plus, Trash2, Edit, Users2, TrendingUp, BadgePercent } from 'lucide-react';

const EMPTY = {
  name: '',
  type: 'automatic',
  code: '',
  discount_mode: 'percent',
  discount_value: 10,
  applies_to: 'all',
  min_order_inr: 0,
  max_discount_inr: null,
  total_usage_limit: null,
  per_user_limit: null,
  first_time_users_only: false,
  valid_from: '',
  valid_until: '',
  banner_text: '',
  banner_link: '',
  active: true,
};

const StatBadge = ({ promo }) => {
  const now = new Date();
  const vu = promo.valid_until ? new Date(promo.valid_until) : null;
  const vf = promo.valid_from ? new Date(promo.valid_from) : null;
  if (!promo.active) return <Badge variant='destructive' className='text-[10px]'>disabled</Badge>;
  if (vu && vu < now) return <Badge variant='outline' className='text-[10px]'>expired</Badge>;
  if (vf && vf > now) return <Badge variant='outline' className='text-[10px]'>scheduled</Badge>;
  return <Badge variant='default' className='text-[10px] bg-[hsl(var(--verified))]/15 text-[hsl(var(--verified))] hover:bg-[hsl(var(--verified))]/20'>live</Badge>;
};

const AdminPromotions = () => {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null); // { ...promo } or null for closed
  const [deleteId, setDeleteId] = useState(null);
  const [redemptionsFor, setRedemptionsFor] = useState(null);
  const [redemptions, setRedemptions] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/admin/promotions');
      setRows(data);
    } catch { toast.error('Failed to load promotions'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const startCreate = () => setEditing({ ...EMPTY });
  const startEdit = (p) => setEditing({ ...p });

  const save = async () => {
    setBusy(true);
    try {
      // Clean payload
      const payload = { ...editing };
      // numeric fields: convert empty strings to null / number
      ['min_order_inr', 'max_discount_inr', 'total_usage_limit', 'per_user_limit'].forEach((k) => {
        if (payload[k] === '' || payload[k] == null) payload[k] = null;
        else if (typeof payload[k] === 'string') payload[k] = Number(payload[k]);
      });
      if (payload.min_order_inr == null) payload.min_order_inr = 0;
      if (payload.discount_value === '' || payload.discount_value == null) payload.discount_value = 0;
      if (payload.type !== 'coupon') payload.code = null;
      if (payload.applies_to !== 'plan_ids') payload.plan_ids = null;

      if (payload.id) {
        const { id, total_used, total_discount_inr, created_at, created_by, updated_at, updated_by, ...body } = payload;
        await api.patch(`/admin/promotions/${id}`, body);
        toast.success('Promotion updated');
      } else {
        await api.post('/admin/promotions', payload);
        toast.success('Promotion created');
      }
      setEditing(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/admin/promotions/${deleteId}`);
      toast.success('Deleted');
      setDeleteId(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const openRedemptions = async (p) => {
    setRedemptionsFor(p);
    setRedemptions([]);
    try {
      const { data } = await api.get(`/admin/promotions/${p.id}/redemptions`);
      setRedemptions(data);
    } catch { toast.error('Failed to load redemptions'); }
  };

  const totalDiscount = rows.reduce((s, p) => s + (p.total_discount_inr || 0), 0);
  const totalUsed = rows.reduce((s, p) => s + (p.total_used || 0), 0);

  return (
    <div className='space-y-4'>
      <div className='flex items-start justify-between gap-3 flex-wrap'>
        <div>
          <h1 className='text-2xl sm:text-3xl font-semibold tracking-tight flex items-center gap-2'>
            <BadgePercent className='h-6 w-6 text-primary' /> Promotions
          </h1>
          <p className='text-muted-foreground text-sm mt-0.5'>
            Automatic offers apply to everyone; coupon codes require entry at checkout. Both support percentage or flat discounts, dates, caps and per-user limits.
          </p>
        </div>
        <Button onClick={startCreate} data-testid='promo-create-btn'>
          <Plus className='h-4 w-4 mr-1.5' /> New promotion
        </Button>
      </div>

      <div className='grid grid-cols-3 gap-3'>
        <Card className='p-4'>
          <div className='text-xs uppercase text-muted-foreground'>Active promotions</div>
          <div className='text-2xl font-semibold mt-1'>{rows.filter(r => r.active).length}</div>
        </Card>
        <Card className='p-4'>
          <div className='text-xs uppercase text-muted-foreground'>Total redemptions</div>
          <div className='text-2xl font-semibold mt-1'>{totalUsed}</div>
        </Card>
        <Card className='p-4'>
          <div className='text-xs uppercase text-muted-foreground'>Total discount given</div>
          <div className='text-2xl font-semibold mt-1'>₹ {totalDiscount.toLocaleString('en-IN')}</div>
        </Card>
      </div>

      <Card className='p-0'>
        <div className='overflow-x-auto'>
          <table className='w-full text-sm' data-testid='promo-table'>
            <thead className='text-left border-b'>
              <tr>
                <th className='py-2 px-4'>Name</th>
                <th className='py-2 px-4'>Type</th>
                <th className='py-2 px-4'>Discount</th>
                <th className='py-2 px-4'>Applies to</th>
                <th className='py-2 px-4'>Validity</th>
                <th className='py-2 px-4'>Usage</th>
                <th className='py-2 px-4'>Status</th>
                <th className='py-2 px-4'>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={8} className='py-10 text-center text-muted-foreground text-sm'>
                  No promotions yet. Click "New promotion" to create your first offer.
                </td></tr>
              )}
              {rows.map(p => (
                <tr key={p.id} className='border-b last:border-b-0 hover:bg-secondary/40' data-testid={`promo-row-${p.id}`}>
                  <td className='py-2 px-4'>
                    <div className='font-medium'>{p.name}</div>
                    {p.banner_text && <div className='text-[11px] text-muted-foreground truncate max-w-xs'>Banner: “{p.banner_text.slice(0, 60)}”</div>}
                  </td>
                  <td className='py-2 px-4'>
                    {p.type === 'coupon' ? (
                      <span className='inline-flex items-center gap-1'><Ticket className='h-3.5 w-3.5' /> <code>{p.code}</code></span>
                    ) : (
                      <span className='inline-flex items-center gap-1 text-muted-foreground'><Zap className='h-3.5 w-3.5' /> auto</span>
                    )}
                  </td>
                  <td className='py-2 px-4'>
                    {p.discount_mode === 'percent' ? `${p.discount_value}%` : `₹${p.discount_value}`}
                    {p.max_discount_inr && p.discount_mode === 'percent' && <span className='text-muted-foreground text-xs'> (max ₹{p.max_discount_inr})</span>}
                  </td>
                  <td className='py-2 px-4 text-xs text-muted-foreground'>{p.applies_to}</td>
                  <td className='py-2 px-4 text-xs text-muted-foreground'>
                    {p.valid_from ? new Date(p.valid_from).toLocaleDateString('en-IN') : '—'} →{' '}
                    {p.valid_until ? new Date(p.valid_until).toLocaleDateString('en-IN') : '∞'}
                  </td>
                  <td className='py-2 px-4 text-xs text-muted-foreground'>
                    {p.total_used || 0}{p.total_usage_limit ? ` / ${p.total_usage_limit}` : ''}
                    {p.total_discount_inr ? <div>₹ {p.total_discount_inr.toLocaleString('en-IN')} saved</div> : null}
                  </td>
                  <td className='py-2 px-4'><StatBadge promo={p} /></td>
                  <td className='py-2 px-4'>
                    <div className='flex gap-1'>
                      <Button size='sm' variant='outline' onClick={() => openRedemptions(p)} data-testid={`promo-redemptions-${p.id}`} title='View redemptions'>
                        <TrendingUp className='h-3.5 w-3.5' />
                      </Button>
                      <Button size='sm' variant='outline' onClick={() => startEdit(p)} data-testid={`promo-edit-${p.id}`} title='Edit'>
                        <Edit className='h-3.5 w-3.5' />
                      </Button>
                      <Button size='sm' variant='ghost' onClick={() => setDeleteId(p.id)} data-testid={`promo-delete-${p.id}`} title='Delete'>
                        <Trash2 className='h-3.5 w-3.5 text-[hsl(var(--danger))]' />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Create / Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(v) => { if (!v) setEditing(null); }}>
        <DialogContent className='max-w-2xl max-h-[85vh] overflow-y-auto' data-testid='promo-edit-dialog' onInteractOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
          {editing && (
            <>
              <DialogHeader>
                <DialogTitle>{editing.id ? 'Edit promotion' : 'New promotion'}</DialogTitle>
              </DialogHeader>
              <div className='grid grid-cols-2 gap-3'>
                <div className='col-span-2'>
                  <Label>Name (internal)</Label>
                  <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid='promo-name-input' />
                </div>
                <div>
                  <Label>Type</Label>
                  <Select value={editing.type} onValueChange={(v) => setEditing({ ...editing, type: v })}>
                    <SelectTrigger data-testid='promo-type-select'><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value='automatic'>Automatic (applies to all)</SelectItem>
                      <SelectItem value='coupon'>Coupon code</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {editing.type === 'coupon' && (
                  <div>
                    <Label>Code</Label>
                    <Input
                      value={editing.code || ''}
                      onChange={(e) => setEditing({ ...editing, code: e.target.value.toUpperCase() })}
                      placeholder='DIWALI25'
                      data-testid='promo-code-input'
                    />
                  </div>
                )}
                <div>
                  <Label>Discount mode</Label>
                  <Select value={editing.discount_mode} onValueChange={(v) => setEditing({ ...editing, discount_mode: v })}>
                    <SelectTrigger data-testid='promo-mode-select'><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value='percent'>Percentage %</SelectItem>
                      <SelectItem value='flat'>Flat ₹ off</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Discount value</Label>
                  <Input type='number' value={editing.discount_value} onChange={(e) => setEditing({ ...editing, discount_value: e.target.value })} data-testid='promo-value-input' />
                </div>
                <div>
                  <Label>Applies to</Label>
                  <Select value={editing.applies_to} onValueChange={(v) => setEditing({ ...editing, applies_to: v })}>
                    <SelectTrigger data-testid='promo-scope-select'><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value='all'>All products</SelectItem>
                      <SelectItem value='subscription'>Subscription plans only</SelectItem>
                      <SelectItem value='ad_pack'>Ad packs only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {editing.discount_mode === 'percent' && (
                  <div>
                    <Label>Max discount cap (₹) <span className='text-muted-foreground text-[11px]'>optional</span></Label>
                    <Input type='number' value={editing.max_discount_inr ?? ''} onChange={(e) => setEditing({ ...editing, max_discount_inr: e.target.value ? Number(e.target.value) : null })} />
                  </div>
                )}
                <div>
                  <Label>Min order (₹)</Label>
                  <Input type='number' value={editing.min_order_inr ?? 0} onChange={(e) => setEditing({ ...editing, min_order_inr: Number(e.target.value) || 0 })} />
                </div>
                <div>
                  <Label>Total usage limit <span className='text-muted-foreground text-[11px]'>optional</span></Label>
                  <Input type='number' value={editing.total_usage_limit ?? ''} onChange={(e) => setEditing({ ...editing, total_usage_limit: e.target.value ? Number(e.target.value) : null })} />
                </div>
                <div>
                  <Label>Per-user limit <span className='text-muted-foreground text-[11px]'>optional</span></Label>
                  <Input type='number' value={editing.per_user_limit ?? ''} onChange={(e) => setEditing({ ...editing, per_user_limit: e.target.value ? Number(e.target.value) : null })} />
                </div>
                <div>
                  <Label>Valid from <span className='text-muted-foreground text-[11px]'>optional</span></Label>
                  <Input type='datetime-local' value={editing.valid_from ? editing.valid_from.slice(0, 16) : ''} onChange={(e) => setEditing({ ...editing, valid_from: e.target.value ? new Date(e.target.value).toISOString() : '' })} />
                </div>
                <div>
                  <Label>Valid until <span className='text-muted-foreground text-[11px]'>optional</span></Label>
                  <Input type='datetime-local' value={editing.valid_until ? editing.valid_until.slice(0, 16) : ''} onChange={(e) => setEditing({ ...editing, valid_until: e.target.value ? new Date(e.target.value).toISOString() : '' })} />
                </div>
                {editing.type === 'automatic' && (
                  <>
                    <div className='col-span-2'>
                      <Label>Banner text (sitewide) <span className='text-muted-foreground text-[11px]'>optional</span></Label>
                      <Textarea rows={2} value={editing.banner_text || ''} onChange={(e) => setEditing({ ...editing, banner_text: e.target.value })} placeholder='🎉 Launch Sale — 25% off till 31 Aug!' data-testid='promo-banner-input' />
                    </div>
                    <div className='col-span-2'>
                      <Label>Banner link <span className='text-muted-foreground text-[11px]'>optional</span></Label>
                      <Input value={editing.banner_link || ''} onChange={(e) => setEditing({ ...editing, banner_link: e.target.value })} placeholder='/pricing' />
                    </div>
                  </>
                )}
                <div className='col-span-2 flex items-center gap-6 pt-2'>
                  <label className='flex items-center gap-2 text-sm'>
                    <Switch checked={editing.first_time_users_only} onCheckedChange={(v) => setEditing({ ...editing, first_time_users_only: v })} data-testid='promo-firsttime-toggle' />
                    First-time users only
                  </label>
                  <label className='flex items-center gap-2 text-sm'>
                    <Switch checked={editing.active} onCheckedChange={(v) => setEditing({ ...editing, active: v })} data-testid='promo-active-toggle' />
                    Active
                  </label>
                </div>
              </div>
              <DialogFooter>
                <Button variant='outline' onClick={() => setEditing(null)}>Cancel</Button>
                <Button onClick={save} disabled={busy || !editing.name} data-testid='promo-save-btn'>
                  {editing.id ? 'Save changes' : 'Create promotion'}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={(v) => { if (!v) setDeleteId(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this promotion?</AlertDialogTitle>
            <AlertDialogDescription>Redemption history will remain, but the promo won't be applied to any new orders. Cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className='bg-[hsl(var(--danger))] text-white' data-testid='promo-delete-confirm'>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Redemptions dialog */}
      <Dialog open={!!redemptionsFor} onOpenChange={(v) => { if (!v) setRedemptionsFor(null); }}>
        <DialogContent className='max-w-2xl max-h-[85vh] overflow-y-auto'>
          <DialogHeader>
            <DialogTitle>Redemptions — {redemptionsFor?.name}</DialogTitle>
          </DialogHeader>
          {redemptions.length === 0 ? (
            <div className='py-8 text-sm text-muted-foreground text-center'>No redemptions yet.</div>
          ) : (
            <div className='overflow-x-auto'>
              <table className='w-full text-sm' data-testid='promo-redemptions-table'>
                <thead className='text-left border-b'>
                  <tr>
                    <th className='py-2 pr-3'>When</th>
                    <th className='py-2 pr-3'>User</th>
                    <th className='py-2 pr-3'>Order</th>
                    <th className='py-2 pr-3'>Discount</th>
                    <th className='py-2 pr-3'>Final</th>
                  </tr>
                </thead>
                <tbody>
                  {redemptions.map(r => (
                    <tr key={r.id} className='border-b last:border-b-0'>
                      <td className='py-2 pr-3 text-xs text-muted-foreground'>
                        {new Date(r.redeemed_at).toLocaleString('en-IN')}
                      </td>
                      <td className='py-2 pr-3'>
                        <div>{r.user_name || '—'}</div>
                        <div className='text-xs text-muted-foreground'>{r.user_email || r.user_id || '—'}</div>
                      </td>
                      <td className='py-2 pr-3 text-xs'>₹ {r.original_inr?.toLocaleString('en-IN')}</td>
                      <td className='py-2 pr-3 text-[hsl(var(--verified))]'>−₹ {r.discount_inr?.toLocaleString('en-IN')}</td>
                      <td className='py-2 pr-3 font-medium'>₹ {r.final_inr?.toLocaleString('en-IN')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPromotions;
